import { useCallback, useMemo } from 'react';
import { CssBaseline } from 'src/components/muiTheme/utils';
import { ThemeProvider as MuiThemeProvider } from 'src/components/muiTheme/utils';

import { createTheme } from '../components/muiTheme/create-theme';

import { useOptionalSession } from './SessionContext';

// Default primary color as specified in PRD
const DEFAULT_PRIMARY_COLOR = '#7560FF';

type Unsubscribe = () => void;

export function ThemeProvider({ children }) {
  const { session, isDarkmode } = useOptionalSession();
  const darkmode = isDarkmode();
  const sessionDarkmode = darkmode ? 'dark' : 'light';

  const theme = useMemo(() => {
    return createTheme({
      primaryColor: session?.theme?.primary || DEFAULT_PRIMARY_COLOR,
      direction: 'ltr',
    });
  }, [session?.theme?.primary]);

  // Custom implementation for MUI Storage Manager
  // to Sync MUIs darkmode with our session context
  const storageManager = useCallback((): {
    get: (defaultValue: any) => any;
    set: (value: any) => void;
    subscribe: (handler: (value: any) => void) => Unsubscribe;
  } => {
    return {
      get: (defaultValue) => {
        return sessionDarkmode || defaultValue;
      },
      set: () => {
        // We dont need to set in the storage manager
        // Only set dark/light mode through session context
        // And let the subscribe update MUI
      },
      subscribe: (handler) => {
        if (sessionDarkmode) {
          handler(sessionDarkmode);
        }
        return () => {
          // Dont need cleanup because we dont actually subscribe
        };
      },
    };
  }, [sessionDarkmode]);

  return (
    <MuiThemeProvider
      theme={theme}
      defaultMode={sessionDarkmode}
      storageManager={storageManager}
      disableTransitionOnChange
      noSsr
    >
      <CssBaseline />
      {children}
    </MuiThemeProvider>
  );
}
